/**
 * SmallPower3D — v2.27 — 3D viewer for the small-power layout.
 *
 * Reuses the `Room` mesh from `scene.js` for the shell, then renders the
 * small-power items (receptacles, luminaires, switches) as primitive meshes
 * positioned in inches and converted via IN_TO_M. Items are NOT
 * draggable — this is a presentation view. Right-click any wall to hide it
 * (same as the main 3D tab).
 *
 * Notable: this file uses `React.createElement` (`h` alias) for the in-Canvas
 * tree because three-fiber primitives (`<mesh>`, `<group>`) MUST be created
 * with `h()` to keep this file parseable by both ESLint and Three's reconciler
 * — see scene.js for the precedent. Plain JSX is used everywhere outside the
 * Canvas.
 */
import React, { useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { Room, IN_TO_M } from "@/components/r3d/scene";
import { OrbitControls } from "@react-three/drei";

const h = React.createElement;

/** Sphere marker for a receptacle. */
function ReceptacleMesh({ item, color = "#22c55e", onSelect, selected }) {
  const x = (item.x_in || 0) * IN_TO_M;
  const z = (item.y_in || 0) * IN_TO_M;
  const y = (item.mount_in || 18) * IN_TO_M;
  const sel = selected === item.id;
  return h("group", { position: [x, y, z], "data-testid": `sp3d-recep-${item.id}` },
    h("mesh", {
      castShadow: true,
      onClick: (e) => { e.stopPropagation(); onSelect && onSelect("receptacle", item); },
      onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
      onPointerOut: () => { document.body.style.cursor = "auto"; },
    },
      h("sphereGeometry", { args: [sel ? 0.06 : 0.04, 16, 12] }),
      h("meshStandardMaterial", {
        color: sel ? "#FFCC00" : color,
        emissive: sel ? "#FFCC00" : color,
        emissiveIntensity: sel ? 0.4 : 0.15,
      }),
    ),
  );
}

/** Flat rectangular box at the ceiling for a luminaire. */
function LuminaireMesh({ project, item, color = "#FFCC00", onSelect, selected }) {
  const x = (item.x_in || 0) * IN_TO_M;
  const z = (item.y_in || 0) * IN_TO_M;
  const ceiling = (project.depth_in || 120) * IN_TO_M;
  const y = ((item.mount_in || (project.depth_in || 120) - 12) * IN_TO_M);
  const yAnchored = Math.min(y, ceiling - 0.02);
  const sel = selected === item.id;
  return h("group", { position: [x, yAnchored, z], "data-testid": `sp3d-lum-${item.id}` },
    h("mesh", {
      castShadow: true,
      onClick: (e) => { e.stopPropagation(); onSelect && onSelect("luminaire", item); },
      onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
      onPointerOut: () => { document.body.style.cursor = "auto"; },
    },
      h("boxGeometry", { args: [0.6, 0.04, 0.3] }),
      h("meshStandardMaterial", {
        color: sel ? "#22c55e" : color,
        emissive: sel ? "#22c55e" : color,
        emissiveIntensity: sel ? 0.6 : 0.35,
      }),
    ),
  );
}

/** Thin vertical cross for a switch. */
function SwitchMesh({ item, color = "#00B8D4", onSelect, selected }) {
  const x = (item.x_in || 0) * IN_TO_M;
  const z = (item.y_in || 0) * IN_TO_M;
  const y = (item.mount_in || 48) * IN_TO_M;
  const sel = selected === item.id;
  return h("group", { position: [x, y, z], "data-testid": `sp3d-sw-${item.id}` },
    h("mesh", {
      onClick: (e) => { e.stopPropagation(); onSelect && onSelect("switch", item); },
      onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
      onPointerOut: () => { document.body.style.cursor = "auto"; },
    },
      h("boxGeometry", { args: [sel ? 0.12 : 0.08, sel ? 0.16 : 0.12, 0.02] }),
      h("meshStandardMaterial", {
        color: sel ? "#FFCC00" : color,
        emissive: sel ? "#FFCC00" : color,
        emissiveIntensity: sel ? 0.5 : 0.25,
      }),
    ),
  );
}

function SmallPowerScene({ project, layout, showShell, onSelect, selected }) {
  const dragPlaneRef = useRef(null);
  // The Room component takes a `room` prop that mirrors project geometry.
  const room = {
    length_in: project.length_in || 240,
    width_in:  project.width_in  || 180,
    depth_in:  project.depth_in  || 120,
  };
  return h("group", null,
    // Lights
    h("ambientLight", { intensity: 0.55 }),
    h("directionalLight", {
      position: [
        (project.length_in || 240) * IN_TO_M * 0.6,
        (project.depth_in || 120) * IN_TO_M * 2,
        (project.width_in || 180) * IN_TO_M * 0.6,
      ],
      intensity: 0.7,
      castShadow: true,
    }),
    // Room shell
    h(Room, {
      room,
      dragPlaneRef,
      snap: 12,
      showFloorGrid: true,
      showWallGrids: false,
      showShell,
      openings: project.wall_openings || [],
      hiddenWalls: project.hidden_walls || [],
      onToggleWall: null,
    }),
    // Receptacles
    (layout.receptacles || []).map((r) =>
      h(ReceptacleMesh, { key: `r-${r.id}`, item: r, onSelect, selected }),
    ),
    // Luminaires
    (layout.luminaires || []).map((l) =>
      h(LuminaireMesh, { key: `l-${l.id}`, project, item: l, onSelect, selected }),
    ),
    // Switches
    (layout.switches || []).map((s) =>
      h(SwitchMesh, { key: `s-${s.id}`, item: s, onSelect, selected }),
    ),
    h(OrbitControls, { enableDamping: true, dampingFactor: 0.08 }),
  );
}

/**
 * Top-level component, rendered inside SmallPowerTab below the SVG plan.
 * v2.28 — click any item to open an inline detail panel; click outside
 * deselects. Edit panel surfaces tag + mount_in for quick tweaks; saving
 * delegates to `onItemEdit(bucket, id, patch)` (passed in from
 * SmallPowerTab → existing editRow handler).
 */
export default function SmallPower3D({ project, layout, onItemEdit }) {
  const [showShell, setShowShell] = useState(true);
  const [selected, setSelected] = useState(null);
  const noRoom = !project.length_in || !project.width_in;
  if (noRoom) return null;

  const totalItems =
    (layout.receptacles?.length || 0)
    + (layout.luminaires?.length  || 0)
    + (layout.switches?.length    || 0);

  const onSelect = (kind, item) => setSelected({ kind, item });
  const onCanvasMissed = () => setSelected(null);

  return (
    <div className="border border-white/10 bg-[#050608] p-3 mt-4" data-testid="small-power-3d">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tag">// Preview · 3D view (v2.28)</div>
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className="text-gray-500">
            click any item to inspect &amp; edit
          </span>
          <button
            onClick={() => setShowShell((v) => !v)}
            className="uppercase tracking-wider text-gray-400 hover:text-cyan-300 transition-colors"
            data-testid="sp3d-toggle-shell"
          >
            {showShell ? "Hide shell" : "Show shell"}
          </button>
        </div>
      </div>
      <div style={{ height: 420 }} className="relative bg-[#0a0d12] border border-white/5">
        <Canvas
          shadows
          camera={{
            position: [
              (project.length_in || 240) * IN_TO_M * 1.2,
              (project.depth_in  || 120) * IN_TO_M * 1.4,
              (project.width_in  || 180) * IN_TO_M * 1.2,
            ],
            fov: 45,
          }}
          onPointerMissed={onCanvasMissed}
          data-testid="sp3d-canvas"
        >
          <SmallPowerScene
            project={project} layout={layout} showShell={showShell}
            onSelect={onSelect} selected={selected?.item?.id || null}
          />
        </Canvas>
        {selected && (
          <SelectedItemPanel
            kind={selected.kind}
            item={selected.item}
            project={project}
            onClose={() => setSelected(null)}
            onItemEdit={onItemEdit}
          />
        )}
      </div>
      <div className="flex gap-4 mt-2 text-[10px] font-mono text-gray-400">
        <span><span className="inline-block w-3 h-3 rounded-full bg-[#22c55e] mr-1 align-middle" /> Receptacle</span>
        <span><span className="inline-block w-3 h-2 bg-[#FFCC00] mr-1 align-middle" /> Luminaire</span>
        <span><span className="text-[#00B8D4] mr-1">+</span> Switch</span>
        <span className="text-gray-500 ml-2">{totalItems} item(s) · drag to orbit · scroll to zoom</span>
      </div>
    </div>
  );
}

function SelectedItemPanel({ kind, item, project, onClose, onItemEdit }) {
  const [mountIn, setMountIn] = useState(item.mount_in ?? 18);
  const bucket = kind === "receptacle" ? "receptacles"
                : kind === "luminaire"  ? "luminaires"
                : "switches";
  const ceiling = project.depth_in || 120;
  const onSave = () => {
    if (!onItemEdit) { onClose(); return; }
    onItemEdit(bucket, item.id, { mount_in: Number(mountIn) });
    onClose();
  };
  return (
    <div
      className="absolute top-3 right-3 z-10 w-64 bg-black/85 backdrop-blur-sm border border-cyan-500/40 p-3 text-xs font-mono text-gray-200"
      data-testid="sp3d-selected-panel"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[#00E5FF] uppercase tracking-wider text-[10px]">
          {kind} · {item.tag}
        </span>
        <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="sp3d-panel-close">×</button>
      </div>
      <div className="space-y-1.5 text-[11px] text-gray-300">
        <div><span className="text-gray-500">Type:</span> {item.label || item.type}</div>
        {item.amps && <div><span className="text-gray-500">Rating:</span> {item.amps}A · {item.volts}V</div>}
        {item.lumens && <div><span className="text-gray-500">Output:</span> {item.lumens} lm · {item.watts} W</div>}
        <div><span className="text-gray-500">Position:</span> X={item.x_in}″ Y={item.y_in}″</div>
        <div className="pt-2 border-t border-white/10">
          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
            Mount height (in) · ceiling {ceiling}″
          </label>
          <input
            type="number" min={0} max={ceiling}
            value={mountIn}
            onChange={(e) => setMountIn(e.target.value)}
            className="w-full bg-black/40 border border-white/10 px-2 py-1 focus:border-cyan-400 outline-none"
            data-testid="sp3d-edit-mount-in"
          />
        </div>
        {item.rule && (
          <div className="pt-2 text-[10px] text-gray-500 italic">{item.rule}</div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onSave} className="btn-primary text-[11px] py-1 px-3 flex-1" data-testid="sp3d-edit-save">
          Apply
        </button>
        <button onClick={onClose} className="btn-ghost text-[11px] py-1 px-3" data-testid="sp3d-edit-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
